import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ind-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-stone-300 disabled:shadow-none whitespace-nowrap rounded-lg",
  {
    variants: {
      variant: {
        primary: "bg-ind-600 text-white hover:bg-ind-700 hover:shadow-md",
        success: "bg-amber-500 text-white hover:bg-amber-700",
        outline:
          "bg-white text-stone-600 border border-stone-300 hover:bg-stone-50 hover:border-stone-400",
        ghost: "bg-transparent text-stone-500 hover:bg-stone-100 hover:text-stone-700",
        danger: "bg-red-500 text-white hover:bg-red-700",
      },
      size: {
        sm: "px-3 py-1.5 text-[0.8rem] rounded-md",
        default: "px-[1.4rem] py-[0.65rem] text-[0.9rem]",
        lg: "px-8 py-[0.8rem] text-[1rem]",
        icon: "h-9 w-9 p-0 rounded-md",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
